/**
 * White-Label Theming System
 * 
 * Allows franchise customers to customize:
 * - Brand colors (primary, secondary, accent)
 * - Logo and favicon
 * - Company name and tagline
 * - Custom CSS variables
 * 
 * Theme is stored per customer/franchise and applied globally
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '@/config/apiConfig';

// Default Corvana theme
const DEFAULT_THEME = {
  id: 'corvana-default',
  name: 'Corvana Default',
  colors: {
    primary: '#6366f1',      // Indigo
    primaryForeground: '#ffffff',
    secondary: '#f1f5f9',
    secondaryForeground: '#0f172a',
    accent: '#8b5cf6',       // Violet
    accentForeground: '#ffffff',
    background: '#ffffff',
    foreground: '#0f172a',
    muted: '#f1f5f9',
    mutedForeground: '#64748b',
    border: '#e2e8f0',
    ring: '#6366f1',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6'
  },
  gradients: {
    primary: 'from-indigo-500 to-purple-500',
    secondary: 'from-slate-100 to-slate-200',
    accent: 'from-violet-500 to-purple-500',
    success: 'from-emerald-500 to-[#0066CC]',
    warning: 'from-amber-500 to-orange-500',
    error: 'from-red-500 to-rose-500'
  },
  branding: {
    companyName: 'Corvana',
    tagline: 'Corvana AI Business Intelligence',
    logo: null,
    favicon: null,
    showPoweredBy: true
  },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    headingFontFamily: 'Inter, system-ui, sans-serif'
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem'
  }
};

// Preset themes for common industries
export const PRESET_THEMES = {
  'qsr-red': {
    id: 'qsr-red',
    name: 'QSR Classic (Red)',
    colors: {
      ...DEFAULT_THEME.colors,
      primary: '#dc2626',
      accent: '#f59e0b',
      ring: '#dc2626'
    },
    gradients: {
      ...DEFAULT_THEME.gradients,
      primary: 'from-red-600 to-red-500',
      accent: 'from-amber-500 to-yellow-500'
    }
  },
  'qsr-green': {
    id: 'qsr-green',
    name: 'QSR Fresh (Green)',
    colors: {
      ...DEFAULT_THEME.colors,
      primary: '#16a34a',
      accent: '#65a30d',
      ring: '#16a34a'
    },
    gradients: {
      ...DEFAULT_THEME.gradients,
      primary: 'from-green-600 to-emerald-500',
      accent: 'from-lime-500 to-green-500'
    }
  },
  'car-wash-blue': {
    id: 'car-wash-blue',
    name: 'Car Wash (Blue)',
    colors: {
      ...DEFAULT_THEME.colors,
      primary: '#0284c7',
      accent: '#06b6d4',
      ring: '#0284c7'
    },
    gradients: {
      ...DEFAULT_THEME.gradients,
      primary: 'from-sky-600 to-blue-500',
      accent: 'from-[#007AFF] to-sky-500'
    }
  },
  'auto-detailing': {
    id: 'auto-detailing',
    name: 'Auto Detailing (Dark)',
    colors: {
      ...DEFAULT_THEME.colors,
      primary: '#1e293b',
      accent: '#f59e0b',
      background: '#0f172a',
      foreground: '#f8fafc',
      muted: '#1e293b',
      mutedForeground: '#94a3b8',
      border: '#334155',
      ring: '#f59e0b'
    },
    gradients: {
      ...DEFAULT_THEME.gradients,
      primary: 'from-slate-800 to-slate-700',
      accent: 'from-amber-500 to-yellow-500'
    }
  },
  'pet-services': {
    id: 'pet-services',
    name: 'Pet Services (Amber)',
    colors: {
      ...DEFAULT_THEME.colors,
      primary: '#d97706',
      accent: '#ec4899',
      ring: '#d97706'
    },
    gradients: {
      ...DEFAULT_THEME.gradients,
      primary: 'from-amber-600 to-yellow-500',
      accent: 'from-pink-500 to-rose-500'
    }
  },
  'healthcare': {
    id: 'healthcare',
    name: 'Healthcare (Teal)',
    colors: {
      ...DEFAULT_THEME.colors,
      primary: '#0d9488',
      accent: '#06b6d4',
      ring: '#0d9488'
    },
    gradients: {
      ...DEFAULT_THEME.gradients,
      primary: 'from-[#0055B5] to-emerald-500',
      accent: 'from-[#007AFF] to-[#0066CC]'
    }
  },
  'childcare': {
    id: 'childcare',
    name: 'Childcare (Pink)',
    colors: {
      ...DEFAULT_THEME.colors,
      primary: '#ec4899',
      accent: '#8b5cf6',
      ring: '#ec4899'
    },
    gradients: {
      ...DEFAULT_THEME.gradients,
      primary: 'from-pink-500 to-rose-500',
      accent: 'from-violet-500 to-purple-500'
    }
  }
};

// Theme Context
const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  applyTheme: () => {},
  resetTheme: () => {},
  presets: PRESET_THEMES,
  isCustomTheme: false
});

export function useTheme() {
  return useContext(ThemeContext);
}

// Neutral/surface tokens are owned by the light/dark CSS in index.css,
// NOT by the white-label theme. In dark mode we must NOT clobber these
// inline (inline styles beat the .dark stylesheet) or the UI renders as
// a light theme on dark backgrounds. White-label only themes brand colours.
const NEUTRAL_SURFACE_KEYS = new Set([
  'background', 'foreground', 'card', 'cardForeground',
  'popover', 'popoverForeground', 'secondary', 'secondaryForeground',
  'muted', 'mutedForeground', 'border', 'input', 'ring'
]);

// Apply CSS variables to document root
function applyCSSVariables(theme) {
  const root = document.documentElement;
  const isDark = root.classList.contains('dark');
  
  // Apply color variables
  Object.entries(theme.colors || {}).forEach(([key, value]) => {
    // Convert camelCase to kebab-case
    const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();

    // `--accent` / `--accent-foreground` are shadcn's SUBTLE hover/highlight
    // tokens (light tint + readable text), NOT a vivid brand colour. Injecting
    // a saturated theme accent (e.g. violet) here turns every hover/menu
    // highlight purple with unreadable text. Defer these to the stylesheet.
    if (key === 'accent' || key === 'accentForeground') {
      root.style.removeProperty(`--${cssKey}`);
      return;
    }
    
    // In dark mode, defer surface tokens to the .dark stylesheet
    if (isDark && NEUTRAL_SURFACE_KEYS.has(key)) {
      root.style.removeProperty(`--${cssKey}`);
      return;
    }
    
    // Convert hex to HSL for Tailwind compatibility
    const hsl = hexToHSL(value);
    if (hsl) {
      root.style.setProperty(`--${cssKey}`, `${hsl.h} ${hsl.s}% ${hsl.l}%`);
    }
  });
  
  // Apply typography
  if (theme.typography?.fontFamily) {
    root.style.setProperty('--font-sans', theme.typography.fontFamily);
  }
  
  // Apply border radius
  Object.entries(theme.borderRadius || {}).forEach(([key, value]) => {
    root.style.setProperty(`--radius-${key}`, value);
  });
}

// Helper to convert hex to HSL
function hexToHSL(hex) {
  if (!hex || !hex.startsWith('#')) return null;
  
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  
  let max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

// Theme Provider Component
export function ThemeProvider({ children, customerId = null }) {
  const [theme, setThemeState] = useState(DEFAULT_THEME);
  const [isCustomTheme, setIsCustomTheme] = useState(false);
  
  // Load customer theme on mount
  useEffect(() => {
    const loadTheme = async () => {
      // First check localStorage for cached theme
      const cachedTheme = localStorage.getItem(`corvana-theme-${customerId || 'default'}`);
      if (cachedTheme) {
        try {
          const parsed = JSON.parse(cachedTheme);
          setThemeState({ ...DEFAULT_THEME, ...parsed });
          setIsCustomTheme(true);
          applyCSSVariables({ ...DEFAULT_THEME, ...parsed });
          return;
        } catch (e) {
          console.error('Failed to parse cached theme:', e);
        }
      }
      
      // If customer ID provided, fetch their theme from API
      if (customerId) {
        try {
          const response = await fetch(`${API_URL}/api/white-label/theme/${customerId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.theme) {
              const mergedTheme = { ...DEFAULT_THEME, ...data.theme };
              setThemeState(mergedTheme);
              setIsCustomTheme(true);
              applyCSSVariables(mergedTheme);
              localStorage.setItem(`corvana-theme-${customerId}`, JSON.stringify(data.theme));
            }
          }
        } catch (error) {
          console.error('Failed to load customer theme:', error);
        }
      }
      
      // Apply default theme
      applyCSSVariables(DEFAULT_THEME);
    };
    
    loadTheme();
  }, [customerId]);
  
  // Re-apply theme whenever dark mode toggles so surface tokens are
  // released to / reclaimed from the .dark stylesheet correctly.
  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'class') {
          applyCSSVariables(theme);
          break;
        }
      }
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [theme]);
  
  const setTheme = useCallback((newTheme) => {
    const mergedTheme = { ...DEFAULT_THEME, ...newTheme };
    setThemeState(mergedTheme);
    setIsCustomTheme(newTheme.id !== 'corvana-default');
    applyCSSVariables(mergedTheme);
    
    // Cache locally
    localStorage.setItem(`corvana-theme-${customerId || 'default'}`, JSON.stringify(newTheme));
  }, [customerId]);
  
  const applyTheme = useCallback(async (themeConfig) => {
    setTheme(themeConfig);
    
    // If customer ID, save to API
    if (customerId) {
      try {
        await fetch(`${API_URL}/api/white-label/theme/${customerId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ theme: themeConfig })
        });
      } catch (error) {
        console.error('Failed to save theme:', error);
      }
    }
  }, [customerId, setTheme]);
  
  const resetTheme = useCallback(() => {
    setThemeState(DEFAULT_THEME);
    setIsCustomTheme(false);
    applyCSSVariables(DEFAULT_THEME);
    localStorage.removeItem(`corvana-theme-${customerId || 'default'}`);
  }, [customerId]);
  
  return (
    <ThemeContext.Provider value={{
      theme,
      setTheme,
      applyTheme,
      resetTheme,
      presets: PRESET_THEMES,
      isCustomTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export default ThemeProvider;
